import { useEffect, useRef, useState, type ReactNode } from "react";
import { enRoutes, esRoutes } from "@/utils/routes";

export type Lang = "es" | "en";

export const EASE = [0.22, 0.61, 0.36, 1] as const;

/** Rutas reales por idioma. Nunca se inventan equivalentes EN. */
export const routesFor = (language: Lang) => (language === "en" ? enRoutes : esRoutes);

/**
 * Sistema de movimiento compartido, basado en CSS.
 * La base es visible siempre: sin JavaScript no hay ninguna regla que oculte
 * el contenido. El movimiento (opacidad + 12 px) es una mejora progresiva que
 * se activa al añadir la clase `v3-reveal-in` desde el cliente, y se anula con
 * `prefers-reduced-motion`. Duración 420–600 ms, una sola vez al entrar.
 */
export const useRevealRef = <T extends HTMLElement>(delay = 0) => {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          el.style.animationDelay = delay ? `${Math.min(delay, 0.24)}s` : "";
          el.classList.add("v3-reveal-in");
          observer.disconnect();
        });
      },
      { threshold: 0.15 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);

  return ref;
};

export const Reveal = ({
  children,
  delay = 0,
  className,
  as: Tag = "div",
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
  as?: "div" | "li" | "section" | "article";
}) => {
  const ref = useRevealRef<HTMLElement>(delay);
  return (
    <Tag className={className} ref={ref as never}>
      {children}
    </Tag>
  );
};

/**
 * Marco de diagrama: traza las conexiones una sola vez al entrar en viewport
 * (<=900 ms) y después queda quieto. Si el observador no existe o falla, el
 * trazo se muestra completo desde el inicio.
 */
export const DiagramFrame = ({
  children,
  className,
  caption,
}: {
  children: ReactNode;
  className?: string;
  caption: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [drawn, setDrawn] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setDrawn(true);
          io.disconnect();
        }
      },
      { threshold: 0.35 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <figure className={className}>
      <div
        ref={ref}
        className="v3-diagram h-[170px] w-full bg-[color:var(--v3-mineral)] p-6 lg:h-[200px]"
        data-draw={drawn ? "true" : undefined}
      >
        {children}
      </div>
      <figcaption className="v3-caption mt-3">{caption}</figcaption>
    </figure>
  );
};

/** Encabezado de escena: número, etiqueta y H2 en la escala luminosa. */
export const SceneHead = ({
  index,
  label,
  title,
  intro,
}: {
  index: string;
  label: string;
  title: ReactNode;
  intro?: ReactNode;
}) => (
  <header className={intro ? "" : "max-w-[54rem]"}>
    <p className="v3-micro">
      <span style={{ color: "var(--v3-blue)" }}>{index}</span>
      <span className="mx-2" aria-hidden>
        ·
      </span>
      {label}
    </p>
    <div
      className={
        intro
          ? "mt-5 grid gap-6 lg:grid-cols-12 lg:items-end lg:gap-14"
          : "mt-5"
      }
    >
      <Reveal className={intro ? "lg:col-span-7" : undefined}>
        <h2 className="v3-display" style={{ fontSize: "clamp(1.75rem, 3.1vw, 2.75rem)" }}>
          {title}
        </h2>
      </Reveal>
      {intro ? (
        <p className="v3-lede lg:col-span-5" style={{ maxWidth: "52ch" }}>
          {intro}
        </p>
      ) : null}
    </div>
  </header>
);

/** Encabezado de página interior en la dirección luminosa. */
export const PageHead = ({
  eyebrow,
  title,
  intro,
  children,
}: {
  eyebrow?: string;
  title: ReactNode;
  intro?: ReactNode;
  children?: ReactNode;
}) => (
  <section className="v3-section bg-[color:var(--v3-mineral)]">
    <div className="v3-shell">
      {eyebrow ? <p className="v3-micro">{eyebrow}</p> : null}
      <h1
        className="v3-display mt-5"
        style={{ fontSize: "clamp(2rem, 3.8vw, 3.3rem)" }}
      >
        {title}
      </h1>
      {intro ? <p className="v3-body mt-6 text-[1.05rem]">{intro}</p> : null}
      {children ? <div className="mt-9">{children}</div> : null}
    </div>
  </section>
);
